import React from 'react';

import BioentitySearchInput, {BioentitySearchInputProps} from 'inventory/BioentitySearchInput';
import {RowScopedSearchConfig, resolveRowScopedFilter} from 'notebook/tables/search-scoping/rowScopedSearch';
import {useRowModel} from 'notebook/tables/store/hooks/useRowModel';

interface RowScopedBioentitySearchInputProps extends BioentitySearchInputProps {
  /** Config describing which sibling cell scopes this column's search and how (see {@link RowScopedSearchConfig}). */
  rowScopedSearch: RowScopedSearchConfig;
}

/**
 * Generic, framework-owned wrapper around {@link BioentitySearchInput} for an EXISTING_BIOENTITY column that
 * opts into row-scoped search (see {@link RowScopedSearchConfig}).
 *
 * It reads the row it is rendered in (via {@link useRowModel}) and, when a scope entity is selected in the
 * configured sibling cell, narrows the picker to entities linked to it by adding the configured filter. When
 * there is no scope entity, no filter is added and the search keeps its full (e.g. tenant-wide) scope.
 *
 * Any product area can enable this behavior by tagging its column with a `rowScopedSearch` config — no
 * product-specific picker component or logic in the notebook-tables editor is required.
 */
export default function RowScopedBioentitySearchInput({
  rowScopedSearch,
  additionalFilters,
  ...bioentitySearchInputProps
}: RowScopedBioentitySearchInputProps): JSX.Element {
  // Narrow selector to avoid re-render churn on the open picker: recompute the scope filter only when the
  // row's constraints or evaluated values change.
  const rowFilter = useRowModel((rowModel) =>
    resolveRowScopedFilter(rowScopedSearch, {
      columnConstraints: rowModel.columnConstraints,
      evaluatedRow: rowModel.evaluatedRow,
    })
  );

  const mergedFilters = rowFilter != null ? [...(additionalFilters ?? []), rowFilter] : additionalFilters;

  return <BioentitySearchInput {...bioentitySearchInputProps} additionalFilters={mergedFilters} />;
}
